import type { ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";
import { getFragmentData } from "@/__generated__";
import type {
  JobScheduleTypeEnum,
  RebookActionFieldsFragment,
  VisitsByFiltersQuery,
} from "@/__generated__/graphql";
import {
  PackageDepartmentEnum,
  PackageServiceTypeEnum,
  RebookActionFieldsFragmentDoc,
  RebookActionType,
  VisitStatusEnum,
} from "@/__generated__/graphql";
import { Button, Card, CardContent, View } from "@/components/ui";
import { isReadyToSchedule } from "@/lib/booking-lib";
import { formatDate } from "@/lib/helpers/date";
import { formatPrice } from "@/lib/helpers/number";
import { handleWhatsappNumber } from "@/lib/platform";
import { packageServiceTypeLabel } from "@/lib/service/helpers";
import { cn } from "@/lib/utils";
import type { Worker } from "@/types/booking";
import { addMonths } from "date-fns";

import { Typography } from "../../typography";
import {
  formatVisitAddress,
  formatVisitSchedule,
  isWithinRatingWindow,
  visitWorkerFullName,
} from "../utils";
import { VisitRating } from "./visit-rating";
import { VisitStatusBadge } from "./visit-status-badge";
import { WorkerAvatar } from "./worker-avatar";

export type VisitData = {
  id: string;
  status: VisitStatusEnum;
  department: PackageDepartmentEnum;
  serviceType: PackageServiceTypeEnum;
  serviceDate: Date;
  startTime: string;
  endTime: string;
  address: string;
  rating: number;
  worker: Worker;
  value: number;
  scheduleType: JobScheduleTypeEnum;
  rebookAction: RebookActionFieldsFragment;
  packageId: string | null;
};

interface Props {
  visitData: VisitData;
  handleViewDetail(visitId: string): void;
  handleSetSchedule?(visitId: string): void;
  handleRateVisit?(visitId: string, rate?: number): void;
  handleRebook?(visitData: VisitData): void;
  handleReportVisit?(visitId: string): void;
}

export function isVisitStatusPending(status: VisitStatusEnum) {
  return [VisitStatusEnum.PendingClientSchedule, VisitStatusEnum.UnableToSchedule].includes(status);
}

export function mapToVisitData(
  visit: VisitsByFiltersQuery["visitsByFilters"]["visits"][number],
): VisitData {
  const job = visit.job;
  const fullAddress = job?.package.fullAddress ?? "";
  const unitNumber = job?.package.unitNumber ?? "";
  const postalCode = job?.package.postalCode ?? "";

  const cardData: VisitData = {
    id: visit.id,
    status: job ? visit.status : VisitStatusEnum.Started,
    department: job?.package.department ?? PackageDepartmentEnum.HomeCleaning,
    serviceType: job?.package.serviceType ?? PackageServiceTypeEnum.HomeCleaning,
    serviceDate: new Date(visit.serviceDate),
    startTime: String(visit.startTime),
    endTime: String(visit.endTime),
    value: visit.clientBillingValue ?? 0,
    address: formatVisitAddress(fullAddress, unitNumber, postalCode),
    worker: {
      id: visit.worker?.id ?? "",
      fullName: visitWorkerFullName(visit.status, visit.worker),
      phoneNumber: visit.worker?.contactNumber ?? null,
      avatarUrl: visit.worker?.avatarUrl ?? null,
      workerRating: visit.worker?.workerRating ?? 0,
    },
    rating: visit.rating ?? 0,
    scheduleType: job?.scheduleType as JobScheduleTypeEnum,
    rebookAction: getFragmentData(RebookActionFieldsFragmentDoc, visit.rebookAction),
    packageId: job?.package.id ?? null,
  };

  return cardData;
}

export function visitShowsRebookButton(visitData: VisitData) {
  const { rebookAction, status } = visitData;
  if (status !== VisitStatusEnum.Completed) return false;
  if (!rebookAction.eligible) return false;
  return (
    rebookAction.type === RebookActionType.BookingSelectSlot ||
    rebookAction.type === RebookActionType.BookingStart ||
    rebookAction.type === RebookActionType.WhatsappInquiry
  );
}

// removed CardContent component for reuse in PreviousBooking component.
export function VisitCardInfo({ visitData }: { visitData: VisitData }) {
  return (
    <View className="flex flex-col">
      <View className="p-4">
        <View className="mb-2 flex flex-row justify-between">
          <Typography variant="body-md">
            {packageServiceTypeLabel(visitData.serviceType, visitData.department)}
          </Typography>
          <Typography variant="body-sm" color="brand-primary">
            {formatPrice(visitData.value, 0)}
          </Typography>
        </View>
        <View className="flex flex-row items-center gap-1">
          <Typography
            variant="body-sm"
            color="foreground-intermediate"
            numberOfLines={1}
            className="min-w-0 max-[400px]:flex-1"
          >
            {formatVisitSchedule(
              visitData.startTime,
              visitData.endTime,
              visitData.serviceDate,
            )}{" "}
          </Typography>
          {isVisitStatusPending(visitData.status) && !isReadyToSchedule(visitData.serviceDate) ? (
            <>
              <Typography variant={"body-sm"} color="foreground-attenuated">
                |{" "}
              </Typography>
              <Typography variant={"body-sm"} color="warning" className="whitespace-nowrap">
                Reschedule opens {formatDate(addMonths(visitData.serviceDate, -1), "MMM dd")}
              </Typography>
            </>
          ) : null}
        </View>
        <Typography variant="body-sm" color="foreground-intermediate" numberOfLines={1}>
          {visitData.address}
        </Typography>
      </View>
      <Divider />
      <View className="flex flex-row justify-between gap-2 px-4 py-2">
        <View className="max-w-[50%]">
          <WorkerAvatar
            fullName={visitData.worker.fullName}
            avatarUrl={visitData.worker.avatarUrl}
          />
        </View>
        <VisitStatusBadge status={visitData.status} />
      </View>
    </View>
  );
}

export function VisitCard({
  visitData,
  handleViewDetail,
  handleSetSchedule,
  handleRebook,
  handleRateVisit,
  handleReportVisit,
}: Props) {
  const [isRebooking, setIsRebooking] = useState(false);

  const handleContactProfessional = () => {
    if (visitData.worker.phoneNumber) {
      handleWhatsappNumber(visitData.worker.phoneNumber);
    }
  };

  const handleRebooking = () => {
    if (isRebooking) return;
    setIsRebooking(true);
    handleRebook?.(visitData);
  };

  const buttonContactProfessional = useMemo(
    () => (
      <Button
        variant="secondary"
        color="success"
        key="buttonContactProfessional"
        fullWidth="full"
        size="md"
        iconSize="md"
        onClick={handleContactProfessional}
        iconName="whatsappIcon"
        iconColor="fill-success"
        children="Contact Professional"
      />
    ),
    [handleContactProfessional],
  );

  const viewDetailButton = useCallback(
    (label = "View Details") => {
      const isPrimaryButton = [
        VisitStatusEnum.Expired,
        VisitStatusEnum.CancelledByClient,
        VisitStatusEnum.CancelledByOps,
        VisitStatusEnum.CancelledByWorker,
        VisitStatusEnum.MissedByWorker,
        VisitStatusEnum.Scheduled,
      ].includes(visitData.status);
      return (
        <Button
          variant={isPrimaryButton ? "primary" : "secondary"}
          color={isPrimaryButton ? "CTA" : "CTA2"}
          key="view-detail-btn"
          size="md"
          fullWidth="full"
          onClick={() => handleViewDetail(visitData.id)}
          children={label}
        />
      );
    },
    [handleViewDetail, visitData.id, visitData.status],
  );

  const renderButton = useMemo(() => {
    const buttons: ReactNode[] = [];

    if (visitData.status === VisitStatusEnum.Started) {
      return [
        <Typography
          variant="body-sm"
          color="foreground-intermediate"
          key="started-message"
          className="max-w-[50%]"
        >
          Please contact the professional something went wrong.
        </Typography>,
        <Button
          variant="secondary"
          color="CTA2"
          key="report"
          iconName="warningCircle"
          iconColor="CTA2"
          iconSize="md"
          fullWidth="full"
          className="max-w-[50%]"
          size="md"
          onClick={() => handleReportVisit?.(visitData.id)}
          children="Report Issue"
        />,
      ];
    }

    if (visitData.status === VisitStatusEnum.Scheduled) {
      if (visitData.worker.phoneNumber) buttons.push(buttonContactProfessional);

      buttons.push(viewDetailButton("Manage Appointment"));
    }

    if (isVisitStatusPending(visitData.status)) {
      buttons.push(viewDetailButton());
      if (isReadyToSchedule(visitData.serviceDate)) {
        buttons.push(
          <Button
            variant="primary"
            color="CTA"
            key="set-schedule-btn"
            className="max-w-[50%]"
            size="md"
            onClick={() => handleSetSchedule?.(visitData.id)}
            fullWidth={{ sm: "full", md: "none" }}
            children={"Set Schedule"}
          />,
        );
      }
    }

    if (
      [
        VisitStatusEnum.Expired,
        VisitStatusEnum.CancelledByClient,
        VisitStatusEnum.CancelledByOps,
        VisitStatusEnum.CancelledByWorker,
        VisitStatusEnum.MissedByWorker,
      ].includes(visitData.status)
    ) {
      buttons.push(
        <Button
          variant="secondary"
          color="CTA2"
          key="report"
          iconName="warningCircle"
          iconColor="CTA2"
          iconSize="md"
          fullWidth="full"
          className="max-w-[50%]"
          size="md"
          onClick={() => handleReportVisit?.(visitData.id)}
          children="Report Issue"
        />,
      );
      buttons.push(viewDetailButton());
    }
    if (visitData.status === VisitStatusEnum.Completed) {
      buttons.push(viewDetailButton());
      if (handleRebook && visitShowsRebookButton(visitData)) {
        buttons.push(
          <Button
            variant="primary"
            color="CTA"
            key="rebook-btn"
            fullWidth={{ sm: "full", md: "none" }}
            className="max-w-[50%]"
            size="md"
            disabled={isRebooking}
            onClick={handleRebooking}
          >
            Rebook
          </Button>,
        );
      }
    }

    return buttons;
  }, [
    visitData.id,
    visitData.status,
    visitData.worker,
    visitData.department,
    buttonContactProfessional,
    viewDetailButton,
    handleSetSchedule,
    handleReportVisit,
    handleRebook,
    isRebooking,
  ]);

  return (
    <Card radius="xl">
      <View onClick={() => handleViewDetail(visitData.id)}>
        <VisitCardInfo visitData={visitData} />
        <Divider />
      </View>
      {renderButton.length !== 0 && (
        <CardContent className="gap-4 pt-0">
          <View
            className={cn(
              "mt-2 flex flex-row justify-end gap-2",
              visitData.rating ? "py-0" : "pt-0",
            )}
          >
            {renderButton}
          </View>
        </CardContent>
      )}
      {visitData.status === VisitStatusEnum.Completed &&
      isWithinRatingWindow(visitData.serviceDate) ? (
        <>
          <Divider />
          <CardContent className="pt-0">
            <VisitRating
              rating={visitData.rating}
              onClick={(rate) => handleRateVisit?.(visitData.id, rate)}
            />
          </CardContent>
        </>
      ) : null}
    </Card>
  );
}

function Divider() {
  return (
    <View className="native:border-b-border native:border-solid border-border-medium mx-4 border-b border-dashed" />
  );
}
